export interface FeatureItem {
  id: number
  name: string
  icon_url: string
  display_order: number
}

export interface FeaturesResponse {
  result: boolean
  data: FeatureItem[]
  message: string
}

export type FeatureType =
  | 'NEARBY_HIGHLIGHT'
  | 'VIEW_TYPE'
  | 'AMENITY'
  | 'BUILDING_FACILITY'
  | 'SELLING_POINT'

export class FeaturesService {
  private static cache: Map<FeatureType, FeatureItem[]> = new Map()

  private static getAuthHeaders(userToken: string) {
    return {
      Authorization: userToken,
      project: 'haup-stable',
      'Content-Type': 'application/json',
    }
  }

  static async getFeatures(type: FeatureType, userToken: string): Promise<FeatureItem[]> {
    // Check cache first
    if (this.cache.has(type)) {
      return this.cache.get(type)!
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_TS_RESTFUL_API_URL}/property/features/${type}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(userToken),
        },
      )

      if (!response.ok) {
        throw new Error(`Failed to fetch ${type} features`)
      }

      const result: FeaturesResponse = await response.json()
      if (result.result && result.data) {
        // Cache the result
        this.cache.set(type, result.data)
        return result.data
      } else {
        throw new Error(result.message || `Failed to fetch ${type} features`)
      }
    } catch (error) {
      console.error(`Error fetching ${type} features:`, error)
      throw error
    }
  }

  static async getAllFeatures(userToken: string): Promise<Map<FeatureType, FeatureItem[]>> {
    const featureTypes: FeatureType[] = [
      'NEARBY_HIGHLIGHT',
      'VIEW_TYPE',
      'AMENITY',
      'BUILDING_FACILITY',
      'SELLING_POINT',
    ]

    try {
      const promises = featureTypes.map((type) => this.getFeatures(type, userToken))
      const results = await Promise.all(promises)

      const featuresMap = new Map<FeatureType, FeatureItem[]>()
      featureTypes.forEach((type, index) => {
        featuresMap.set(type, results[index])
      })

      return featuresMap
    } catch (error) {
      console.error('Error fetching all features:', error)
      throw error
    }
  }

  static getFeatureIdsByNames(features: FeatureItem[], selectedNames: string[]): number[] {
    return selectedNames
      .map((name) => features.find((feature) => feature.name === name)?.id)
      .filter((id): id is number => id !== undefined)
  }

  static async getSelectedFeatureIds(
    formData: {
      nearbyHighlights: string[]
      viewType: string[]
      amenities: string[]
      buildingFacilities: string[]
      uniqueSellingPoints: string[]
    },
    userToken: string,
  ): Promise<number[]> {
    try {
      const featuresMap = await this.getAllFeatures(userToken)

      const selectedIds: number[] = []

      // Map nearby highlights
      if (formData.nearbyHighlights?.length > 0) {
        const nearbyFeatures = featuresMap.get('NEARBY_HIGHLIGHT') || []
        const ids = this.getFeatureIdsByNames(nearbyFeatures, formData.nearbyHighlights)
        selectedIds.push(...ids)
      }

      // Map view types
      if (formData.viewType?.length > 0) {
        const viewFeatures = featuresMap.get('VIEW_TYPE') || []
        const ids = this.getFeatureIdsByNames(viewFeatures, formData.viewType)
        selectedIds.push(...ids)
      }

      // Map amenities
      if (formData.amenities?.length > 0) {
        const amenityFeatures = featuresMap.get('AMENITY') || []
        const ids = this.getFeatureIdsByNames(amenityFeatures, formData.amenities)
        selectedIds.push(...ids)
      }

      // Map building facilities
      if (formData.buildingFacilities?.length > 0) {
        const facilityFeatures = featuresMap.get('BUILDING_FACILITY') || []
        const ids = this.getFeatureIdsByNames(facilityFeatures, formData.buildingFacilities)
        selectedIds.push(...ids)
      }

      // Map unique selling points (now using SELLING_POINT)
      if (formData.uniqueSellingPoints?.length > 0) {
        const sellingPointFeatures = featuresMap.get('SELLING_POINT') || []
        const ids = this.getFeatureIdsByNames(sellingPointFeatures, formData.uniqueSellingPoints)
        selectedIds.push(...ids)
      }

      return selectedIds
    } catch (error) {
      console.error('Error getting selected feature IDs:', error)
      return []
    }
  }
}
